import React from "react";
import { NavLink, Redirect, Route, Switch, useLocation, useHistory } from "react-router-dom";
import { useProfitAuthActions } from "../auth/ProfitAuthContext";
import { profitNavItems, profitPortalMeta } from "../data/profitData";
import ProfitDashboardPage from "../pages/ProfitDashboardPage";
import ProfitMerchantsPage from "../pages/ProfitMerchantsPage";
import ProfitNotFoundPage from "../pages/ProfitNotFoundPage";
import ProfitPayoutsPage from "../pages/ProfitPayoutsPage";
import ProfitReportsPage from "../pages/ProfitReportsPage";
import ProfitRevenuePage from "../pages/ProfitRevenuePage";
import ProfitSettingsPage from "../pages/ProfitSettingsPage";
import {
  ProfitBellIcon,
  ProfitSearchIcon,
  ProfitSignOutIcon,
} from "../components/ProfitIcons";

const PROFIT_THEME_KEY = "profit_portal_theme";
const PROFIT_LOGO_URL = process.env.PUBLIC_URL + "/merchant-login-logo.webp";

function getInitialProfitDarkMode() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(PROFIT_THEME_KEY) === "dark";
}

export default function ProfitLayout() {
  const history = useHistory();
  const location = useLocation();
  const { logout } = useProfitAuthActions();
  const [isDarkMode, setIsDarkMode] = React.useState(getInitialProfitDarkMode);

  React.useEffect(
    function syncProfitTheme() {
      if (typeof window === "undefined") {
        return;
      }

      window.localStorage.setItem(PROFIT_THEME_KEY, isDarkMode ? "dark" : "light");
    },
    [isDarkMode],
  );

  const activeNavItem = profitNavItems.find(function findItem(item) {
    return location.pathname.indexOf(item.path) === 0;
  });

  function handleLogout() {
    logout();
    history.push("/profit/login");
  }

  return (
    <div className={"profit-shell profit-portal-shell" + (isDarkMode ? " profit-dark" : "")}>
      <aside className="profit-sidebar">
        <div className="profit-sidebar-brand">
          <img className="profit-logo-image profit-logo-image-small" src={PROFIT_LOGO_URL} alt="Uniweb" />
          <div>
            <div className="profit-brand-name">Uniweb Pte. Ltd.</div>
            <div className="profit-brand-sub">Partner Portal</div>
          </div>
        </div>
        <div className="profit-account-card">
          <div className="profit-account-label">Partner Account</div>
          <div className="profit-account-name">{profitPortalMeta.companyName}</div>
          <div className="profit-account-id">
            <span className="profit-mono">{profitPortalMeta.accountId}</span>
            <span className="profit-tier-badge profit-tier-gold">
              {profitPortalMeta.partnerTier}
            </span>
          </div>
        </div>
        <nav className="profit-sidebar-nav">
          {profitNavItems.map(function renderNavItem(item) {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.key}
                to={item.path}
                className="profit-nav-item"
                activeClassName="active"
              >
                <Icon />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="profit-sidebar-footer">
          <button
            className="profit-toggle-row"
            type="button"
            onClick={function handleToggle() {
              setIsDarkMode(!isDarkMode);
            }}
          >
            <span className={"profit-toggle-switch" + (isDarkMode ? " on" : "")}>
              <span className="profit-toggle-knob" />
            </span>
            <span>{isDarkMode ? "Light mode" : "Dark mode"}</span>
          </button>
          <button className="profit-sign-out-row" type="button" onClick={handleLogout}>
            <ProfitSignOutIcon />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      <main className="profit-main">
        <header className="profit-header">
          <div className="profit-header-title">
            {activeNavItem ? activeNavItem.title : "Partner Portal"}
          </div>
          <div className="profit-search-box">
            <ProfitSearchIcon />
            <input type="text" placeholder="Search merchants, revenue…" />
          </div>
          <div className="profit-notification-wrap">
            <button className="profit-icon-button" type="button">
              <ProfitBellIcon />
            </button>
            <span className="profit-notification-dot" />
          </div>
          <div className="profit-avatar">{profitPortalMeta.avatar}</div>
        </header>

        <div className="profit-content">
          <Switch>
            <Route exact path="/profit/dashboard" component={ProfitDashboardPage} />
            <Route exact path="/profit/merchants" component={ProfitMerchantsPage} />
            <Route exact path="/profit/revenue" component={ProfitRevenuePage} />
            <Route exact path="/profit/payouts" component={ProfitPayoutsPage} />
            <Route exact path="/profit/reports" component={ProfitReportsPage} />
            <Route exact path="/profit/settings" component={ProfitSettingsPage} />
            <Route exact path="/profit">
              <Redirect to="/profit/dashboard" />
            </Route>
            <Route component={ProfitNotFoundPage} />
          </Switch>
        </div>

        <footer className="profit-footer">
          <span>{profitPortalMeta.footer}</span>
          <span>{profitPortalMeta.systemStatus}</span>
        </footer>
      </main>
    </div>
  );
}
